// ======================================================================
// File: src/modules/ipe/costing/SellingCostingOverlayDrawer.js
// ======================================================================

import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import axiosClient from "../../../api/axiosClient";

export default function SellingCostingOverlayDrawer({ enquiryId, onClose }) {

  const [mode, setMode] = useState("COMBINED");
  const [view, setView] = useState("CATEGORY");
  const [loading, setLoading] = useState(false);

  const [header, setHeader] = useState(null);
  const [categoryLines, setCategoryLines] = useState([]);
  const [skuLines, setSkuLines] = useState([]);

  const [globalMargin, setGlobalMargin] = useState(10);
  const [categoryMargins, setCategoryMargins] = useState({});
  const [skuMargins, setSkuMargins] = useState({});

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = "auto"; };
  }, []);

  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-IN").format(Number(num || 0));
  };

  const SummaryCard = ({ title, value, highlight, positive }) => (
    <div style={{
      background: "#fff",
      padding: "10px 16px",
      borderRadius: 8,
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      minWidth: 160
    }}>
      <div style={{ fontSize: 12, color: "#666" }}>{title}</div>
      <div style={{
        fontSize: 18,
        fontWeight: 600,
        color: highlight ? (positive ? "green" : "red") : "#111"
      }}>
        ₹ {formatNumber(value)}
      </div>
    </div>
  );

  const loadSellingCost = async () => {
    try {
      const res = await axiosClient.get(`/api/ipe/selling-cost/enquiry/${enquiryId}`);
      const data = res.data.data;
      if (!data) return;

      setHeader(data.header);
      setCategoryLines(data.category_lines || []);
      setSkuLines(data.sku_lines || []);
    } catch (err) {
      console.error("Load failed", err);
    }
  };

  useEffect(() => {
    if (enquiryId) loadSellingCost();
  }, [enquiryId]);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      await axiosClient.post("/api/ipe/selling-cost/generate", {
        enquiry_id: enquiryId,
        mode
      });
      alert("Generated successfully ✅");
      await loadSellingCost();
    } catch {
      alert("Generate failed ❌");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        category_lines: categoryLines.map(row => ({
          id: row.id,
          margin_percent: getCategoryMargin(row.category),
          margin_amount: getCategoryMarginAmount(row.total_cost, row.category),
          selling_price: getCategorySellingPrice(row.total_cost, row.category)
        })),
        sku_lines: skuLines.map(row => ({
          id: row.id,
          margin_percent: getSkuMargin(row.id),
          margin_amount: getSkuMarginAmount(row.total_cost, row.id),
          selling_price: getSkuSellingPrice(row.total_cost, row.id)
        }))
      };

      await axiosClient.post(`/api/ipe/selling-cost/${header.id}/save`, payload);
      alert("Saved successfully ✅");
      await loadSellingCost();
    } catch {
      alert("Save failed ❌");
    }
  };

  const handleSubmit = async () => {
    try {
      await axiosClient.post(`/api/ipe/selling-cost/${header.id}/submit`);
      alert("Submitted successfully ✅");
      await loadSellingCost();
    } catch (err) {
      alert(err?.response?.data?.message || "Submit failed ❌");
    }
  };

  const handleApprove = async () => {
    try {
      await axiosClient.post(`/api/ipe/selling-cost/${header.id}/approve`);
      alert("Approved successfully ✅");
      await loadSellingCost();
    } catch (err) {
      alert(err?.response?.data?.message || "Approve failed ❌");
    }
  };

  const handleReject = async () => {
    const remarks = prompt("Enter rejection remarks:");
    if (!remarks) return;

    try {
      await axiosClient.post(`/api/ipe/selling-cost/${header.id}/reject`, { remarks });
      alert("Rejected ❌");
      await loadSellingCost();
    } catch (err) {
      alert(err?.response?.data?.message || "Reject failed ❌");
    }
  };

  const isEditable = header?.status === "draft";

  const getCategoryMargin = (category) => categoryMargins[category] ?? globalMargin;
  const getCategoryMarginAmount = (cost, category) =>
    (cost * getCategoryMargin(category)) / 100;
  const getCategorySellingPrice = (cost, category) =>
    cost + getCategoryMarginAmount(cost, category);

  const getSkuMargin = (id) => skuMargins[id] ?? globalMargin;
  const getSkuMarginAmount = (cost, id) =>
    (cost * getSkuMargin(id)) / 100;
  const getSkuSellingPrice = (cost, id) =>
    cost + getSkuMarginAmount(cost, id);

  const profit = (header?.total_selling_price || 0) - (header?.total_cost || 0);

  const drawerUI = (
    <div style={{
      position: "fixed",
      top: 0,
      right: 0,
      width: "100vw",
      height: "100vh",
      background: "#fff",
      zIndex: 9999,
      display: "flex",
      flexDirection: "column"
    }}>

      {/* HEADER */}
      <div style={{
        background: "linear-gradient(90deg, #5f0cff, #7b2cff)",
        color: "#fff",
        padding: "14px 24px",
        display: "flex",
        justifyContent: "space-between"
      }}>
        <div>
          <b>Selling Costing</b>{" "}
          <span className="badge bg-light text-dark">Version {header?.version_no || 1}</span>{" "}
          <span className="badge bg-secondary">{header?.status?.toUpperCase()}</span>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-light btn-sm" onClick={handleSave} disabled={!isEditable}>Save</button>
          <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={header?.status !== "draft"}>Submit</button>
          <button className="btn btn-success btn-sm" onClick={handleApprove} disabled={header?.status !== "submitted"}>Approve</button>
          <button className="btn btn-danger btn-sm" onClick={handleReject} disabled={header?.status !== "submitted"}>Reject</button>
          <button className="btn btn-outline-light btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>

      {/* SUMMARY CARDS */}
      {header && (
        <div style={{
          display: "flex",
          gap: 20,
          padding: "12px 24px",
          background: "#f8f9fc",
          borderBottom: "1px solid #e5e7eb"
        }}>
          <SummaryCard title="Total Cost" value={header.total_cost} />
          <SummaryCard title="Selling Price" value={header.total_selling_price} />
          <SummaryCard title="Profit" value={profit} highlight positive={profit >= 0} />
        </div>
      )}

      {/* CONTROLS */}
      <div style={{
        padding: 16,
        display: "flex",
        gap: 10,
        alignItems: "center",
        borderBottom: "1px solid #eee"
      }}>
        <select value={mode} onChange={e => setMode(e.target.value)}>
          <option value="COMBINED">Combined</option>
          <option value="ITEM_WISE">Item-wise</option>
        </select>

        <select value={view} onChange={e => setView(e.target.value)}>
          <option value="CATEGORY">Category View</option>
          <option value="SKU">SKU View</option>
        </select>

        <button className="btn btn-primary" onClick={handleGenerate}>
          {loading ? "Generating..." : "Generate"}
        </button>

        <button className="btn btn-outline-secondary" onClick={loadSellingCost}>
          Refresh
        </button>
      </div>

      {/* BODY */}
      <div style={{ padding: 20, overflow: "auto" }}>

        {/* CATEGORY VIEW */}
        {view === "CATEGORY" && (
          <table className="table table-bordered">
            <thead style={{
              position: "sticky",
              top: 0,
              background: "#f5f7fa",
              zIndex: 2
            }}>
              <tr>
                <th>#</th>
                <th>Category</th>
                <th style={{ textAlign: "right" }}>Qty</th>
                <th style={{ textAlign: "right" }}>Total Cost</th>
                <th style={{ textAlign: "center" }}>Margin %</th>
                <th style={{ textAlign: "right" }}>Margin</th>
                <th style={{ textAlign: "right" }}>Selling</th>
              </tr>
            </thead>
            <tbody>
              {categoryLines.map((row, i) => {
                const margin = getCategoryMargin(row.category);
                const marginAmount = getCategoryMarginAmount(row.total_cost, row.category);
                const sellingPrice = getCategorySellingPrice(row.total_cost, row.category);

                return (
                  <tr key={i} style={{ cursor: "pointer" }}>
                    <td>{i + 1}</td>
                    <td><b>{row.category}</b></td>
                    <td style={{ textAlign: "right" }}>{formatNumber(row.quantity)}</td>
                    <td style={{ textAlign: "right" }}>{formatNumber(row.total_cost)}</td>
                    <td style={{ textAlign: "center" }}>
                      <input
                        type="number"
                        style={{
                          width: 60,
                          textAlign: "center",
                          borderRadius: 6,
                          border: "1px solid #ccc"
                        }}
                        disabled={!isEditable}
                        value={margin}
                        onChange={(e) =>
                          setCategoryMargins(prev => ({
                            ...prev,
                            [row.category]: Number(e.target.value)
                          }))
                        }
                      />
                    </td>
                    <td style={{ textAlign: "right" }}>{formatNumber(marginAmount)}</td>
                    <td style={{ textAlign: "right", fontWeight: 700 }}>
                      {formatNumber(sellingPrice)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* SKU VIEW */}
        {view === "SKU" && (
          <table className="table table-bordered">
            <thead style={{
              position: "sticky",
              top: 0,
              background: "#f5f7fa",
              zIndex: 2
            }}>
              <tr>
                <th>#</th>
                <th>SKU</th>
                <th>Item</th>
                <th>Category</th>
                <th>Unit</th>
                <th style={{ textAlign: "right" }}>Qty</th>
                <th style={{ textAlign: "right" }}>Unit Cost</th>
                <th style={{ textAlign: "right" }}>Total Cost</th>
                <th style={{ textAlign: "center" }}>Margin %</th>
                <th style={{ textAlign: "right" }}>Margin</th>
                <th style={{ textAlign: "right" }}>Selling</th>
              </tr>
            </thead>
            <tbody>
              {skuLines.map((row, i) => {
                const margin = getSkuMargin(row.id);
                const marginAmount = getSkuMarginAmount(row.total_cost, row.id);
                const sellingPrice = getSkuSellingPrice(row.total_cost, row.id);

                return (
                  <tr key={i}
                    style={{
                      background: row.unit_cost === 0 ? "#fff3f3" : "",
                      cursor: "pointer"
                    }}>
                    <td>{i + 1}</td>
                    <td>{row.sku_code}</td>
                    <td>{row.item_name}</td>
                    <td>{row.category}</td>
                    <td>{row.unit}</td>
                    <td style={{ textAlign: "right" }}>{formatNumber(row.quantity)}</td>
                    <td style={{ textAlign: "right" }}>{formatNumber(row.unit_cost)}</td>
                    <td style={{ textAlign: "right" }}>{formatNumber(row.total_cost)}</td>
                    <td style={{ textAlign: "center" }}>
                      <input
                        type="number"
                        style={{
                          width: 60,
                          textAlign: "center",
                          borderRadius: 6,
                          border: "1px solid #ccc"
                        }}
                        disabled={!isEditable}
                        value={margin}
                        onChange={(e) =>
                          setSkuMargins(prev => ({
                            ...prev,
                            [row.id]: Number(e.target.value)
                          }))
                        }
                      />
                    </td>
                    <td style={{ textAlign: "right" }}>{formatNumber(marginAmount)}</td>
                    <td style={{ textAlign: "right", fontWeight: 700 }}>
                      {formatNumber(sellingPrice)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* BOTTOM SUMMARY */}
        {header && (
          <div style={{
            marginTop: 30,
            borderTop: "2px solid #eee",
            paddingTop: 15,
            fontWeight: 600,
            textAlign: "right"
          }}>
            <div>Total Cost: ₹ {formatNumber(header.total_cost)}</div>
            <div>Total Selling Price: ₹ {formatNumber(header.total_selling_price)}</div>
          </div>
        )}

      </div>
    </div>
  );

  return ReactDOM.createPortal(drawerUI, document.body);
}